import React, {useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {ArrowBack, ErrorOutline} from '@mui/icons-material';
import {Box, Button, Typography, Divider, Stack} from '@mui/material';
import Loading from '$components/Loading';
import {
  usePaymentDetails,
  useUpdatePaymentDescription,
  useRemoveDiscount,
} from './hooks';
import {PAYMENT_DETAILS_STRINGS} from './constant';
import {PaymentStatusInput} from '$modules/frontdesk/utils';

import {
  containerStyles,
  contentWrapperStyles,
  backButtonStyles,
  verticalDividerStyles,
  detailsContainerStyles,
  headerNameStyles,
} from './styles';

import PaymentDetailsButtons from './PaymentDetailsButtons';
import {Color} from '~/constants/style';
import {PaymentDetails, RefundDetails} from './types';
import FrontdeskButton from '$components/FrontdeskButton';
import PlusSquareIcon from '$assets/svgs/plus-square.svg?react';
import useAuthUser from '~/hooks/useAuthUser';
import {UserRoleName} from '$modules/admin/constant';
import {RoutePath} from '~/constants/routes';
import {RouteSection, getRoute} from '~/utils/route';
import DiscountIcon from '$assets/svgs/discount-icon.svg?react';
import SearchPaymentItem from '../../common/components/AddPaymentItem';
import PaymentItemsList from './PaymentItemsList';
import Timeline from './Timeline';
import ApplyDiscountContainer from './ApplyDiscount';
import PaymentSummary from '../../common/components/PaymentSummary';

interface PaymentDetailsProps {
  amount: string;
  status: PaymentStatusInput;
  description: string | null;
  paymentId?: string;
  payment: PaymentDetails;
  refundDetails?: RefundDetails;
}

const PaymentDetailsSection: React.FC<PaymentDetailsProps> = ({
  payment,
  refundDetails,
}) => {
  const {roleName} = useAuthUser()!;
  const isFinanceRole = roleName === UserRoleName.Finance;
  const {updatePaymentDescription, isPending} = useUpdatePaymentDescription();

  const handleEditItemDescription = (
    itemId: number,
    newDescription: string
  ) => {
    updatePaymentDescription({
      id: itemId.toString(),
      description: newDescription,
    });
  };

  return (
    <Box sx={detailsContainerStyles}>
      <PaymentItemsList
        items={payment.items}
        isFinanceRole={isFinanceRole}
        onEditItemDescription={handleEditItemDescription}
        isUpdating={isPending}
        refundDetails={refundDetails}
      />
    </Box>
  );
};

interface IRefundPaymentDetails {
  payment: PaymentDetails;
}

const RefundPaymentDetails = ({payment}: IRefundPaymentDetails) => {
  const fields = [
    {
      label: 'Reason for refund',
      value: payment?.refund_details?.reason || '-',
    },
    ...(payment?.refund_details?.notes
      ? [
          {
            label: 'Notes',
            value: payment.refund_details.notes,
          },
        ]
      : []),
  ];

  if (!payment.refund_details) {
    return null;
  }

  return (
    <Box className="flex flex-col gap-4 bg-white rounded-xl p-6 my-8">
      <Box className="flex flex-row gap-5">
        <Box className="bg-[#d9daff] h-fit p-[6px] rounded-[8px] flex justify-center items-center">
          <ErrorOutline
            className="text-[#6A0DAD] bg-[#d9daff] flex justify-center items-center"
            fontSize="large"
          />
        </Box>

        <Stack spacing={2} mt={1}>
          <Typography variant="h4" color={Color.Purple600}>
            {PAYMENT_DETAILS_STRINGS.PAYMENT_REFUNDED}
          </Typography>
          {fields.map(({label, value}) => {
            return <InfoField key={label} label={label} value={value} />;
          })}
        </Stack>
      </Box>
    </Box>
  );
};

const InfoField = ({
  label,
  value,
}: {
  label: string;
  value: string | number | undefined;
}) => (
  <Stack spacing={1}>
    <Typography variant="careTitle" sx={{mb: 1}}>
      {label}
    </Typography>
    <Typography sx={{color: 'black', fontSize: '16px', fontWeight: '600'}}>
      {value}
    </Typography>
  </Stack>
);

export const PaymentDetailsComponent: React.FC = () => {
  const navigate = useNavigate();

  const {id} = useParams<{id: string}>();
  const {payment, isLoading, isError, message} = usePaymentDetails(id || '');
  const [isSearchPaymentItemOpen, setIsSearchPaymentItemOpen] = useState(false);
  const [applyDiscountModalOpen, setApplyDiscountModalOpen] =
    useState<boolean>(false);
  const patientId = payment?.patient_id;
  const {roleName} = useAuthUser()!;
  const isFinanceRole = roleName === UserRoleName.Finance;
  const isUnpaid = String(payment?.status) === 'unpaid';
  const isPaid =
    String(payment?.status) === 'paid' ||
    String(payment?.status) === 'succeeded' ||
    String(payment?.status) === 'refunded' ||
    String(payment?.status) === 'refund_requested';
  const discountApplied = Number(payment?.discount_percentage) > 0;
  const {removeDiscountRequest, isPending: isRemovingDiscount} =
    useRemoveDiscount();
  const hasAmount = !!payment?.amount && Number(payment.amount) > 0;
  const isRefunded = String(payment?.status) === 'refunded';
  const isRefundRequested = String(payment?.status) === 'refund_requested';

  if (isLoading) {
    return <Loading />;
  }

  if (isError || !payment) {
    return <div>{message || PAYMENT_DETAILS_STRINGS.NOT_FOUND}</div>;
  }

  const {timeline} = payment;
  const hasTimeline = timeline && timeline.length > 0;

  const handleBackClick = () => {
    navigate(
      getRoute(RouteSection.Finance, RoutePath.staff.finance.paymentList)
    );
  };

  const handleApplyDiscount = () => {
    setApplyDiscountModalOpen(true);
  };

  const handleViewFinancialRecord = () => {
    if (!patientId) return;

    navigate(
      getRoute(RouteSection.Finance, RoutePath.staff.finance.financialRecord),
      {
        state: {patient_id: patientId},
      }
    );
  };

  const canAddItems = isFinanceRole && isUnpaid && !payment.invoice_issued_at;

  return (
    <Box sx={containerStyles}>
      <Box sx={contentWrapperStyles}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBackClick}
          sx={backButtonStyles}
        >
          Back
        </Button>
        <Divider sx={verticalDividerStyles} />

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography sx={headerNameStyles}>
            {payment.patient_full_name}
          </Typography>
          <Box display="flex" gap={2}>
            {isFinanceRole && isUnpaid && canAddItems && (
              <FrontdeskButton
                variant="primary"
                title="Add item"
                startIcon={<PlusSquareIcon width={20} height={20} />}
                className="w-fit font-normal text-[14px]"
                onClick={() => setIsSearchPaymentItemOpen(true)}
              />
            )}
            {!isPaid &&
              isFinanceRole &&
              !discountApplied &&
              hasAmount &&
              !isRefunded &&
              !isRefundRequested && (
                <FrontdeskButton
                  variant="tertiary-bold"
                  title="Apply discount"
                  onClick={handleApplyDiscount}
                  startIcon={<DiscountIcon />}
                />
              )}
            {!isPaid && isFinanceRole && discountApplied && hasAmount && (
              <FrontdeskButton
                variant="tertiary"
                title={isRemovingDiscount ? 'Removing...' : 'Remove discount'}
                onClick={() => removeDiscountRequest(id || '')}
                disabled={isRemovingDiscount}
                startIcon={<DiscountIcon />}
              />
            )}
            <FrontdeskButton
              variant="primary"
              title={PAYMENT_DETAILS_STRINGS.FINANCIAL_RECORD}
              onClick={handleViewFinancialRecord}
            />
          </Box>
        </Box>
        <Divider sx={verticalDividerStyles} />

        <PaymentSummary
          amount={payment.amount}
          status={payment.status}
          lastUpdate={payment.billing_date}
          paymentMethodType={payment.payment_method}
          invoiceNumber={payment.invoice_number}
          updatedAmount={payment.updated_amount}
          discount={payment?.discounted_amount || 0}
          paymentLink={payment.payment_link}
        />

        <Divider sx={verticalDividerStyles} />

        <RefundPaymentDetails payment={payment} />

        <PaymentDetailsSection
          amount={payment.amount}
          status={payment.status}
          description={payment.description}
          paymentId={id}
          payment={payment}
          refundDetails={payment.refund_details}
        />
        <Divider className="mt-2" />
      </Box>
      {hasTimeline && <Timeline timeline={timeline} />}

      <PaymentDetailsButtons paymentId={id} payment={payment} />
      <SearchPaymentItem
        isOpen={isSearchPaymentItemOpen}
        onClose={() => setIsSearchPaymentItemOpen(false)}
        onSelect={() => {
          setIsSearchPaymentItemOpen(false);
        }}
        paymentId={Number(id)}
      />
      {applyDiscountModalOpen && (
        <ApplyDiscountContainer
          applyDiscountModalOpen={applyDiscountModalOpen}
          setApplyDiscountModalOpen={setApplyDiscountModalOpen}
          currentAmount={payment.amount}
          paymentId={id}
        />
      )}
    </Box>
  );
};

export default PaymentDetailsComponent;
